import { Add, Edit, Remove, Sync } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { Formik, getIn } from "formik";
import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { PanelSessionContext } from "../../components/sessionContext";
import { Mirror } from "../../utils/types";
import { addNotification } from "../global/notificationWrapper";

const validationSchema = yup.object().shape({
  name: yup.string().required("Name is required").min(4).max(50),
  scriptPath: yup.string().required("Script path is required").min(4).max(100),
  settings: yup.object().shape({
    assetIdInterval: yup
      .array()
      .of(
        yup
          .number()
          .positive("Asset ID must be positive")
          .integer("Asset ID must be integer"),
      )
      .length(2)
      .test(
        "is-valid-interval",
        "Max asset ID must be > min asset ID",
        (val) => (Array.isArray(val) ? (val[1] || -1) > (val[0] || -1) : false),
      ),
    containerIdInterval: yup
      .array()
      .of(
        yup
          .number()
          .positive("Container ID must be positive")
          .integer("Container ID must be integer"),
      )
      .length(2)
      .test(
        "is-valid-interval",
        "Max container ID must be > min container ID",
        (val) => (Array.isArray(val) ? (val[1] || -1) > (val[0] || -1) : false),
      ),
  }),
});

export const MirrorDialogs = {
  Create: () => {
    const session = useContext(PanelSessionContext);

    const [open, setOpen] = useState(false);
    const [deleteCollidingObjects, setDeleteCollidingObjects] = useState(false);
    const [submitDisabled, setSubmitDisabled] = useState(false);

    const submit = async (values: Mirror) => {
      // check if assetIdInterval and containerIdInterval are colliding with existing mirrors
      for (const mirror of session.mirrors) {
        if (
          (values.settings.assetIdInterval[0] <=
            mirror.settings.assetIdInterval[1] &&
            values.settings.assetIdInterval[1] >=
              mirror.settings.assetIdInterval[0]) ||
          (values.settings.containerIdInterval[0] <=
            mirror.settings.containerIdInterval[1] &&
            values.settings.containerIdInterval[1] >=
              mirror.settings.containerIdInterval[0])
        ) {
          addNotification({
            severity: "error",
            message:
              "Asset ID or Container ID intervals collide with an existing mirror.",
          });
          return;
        }
      }

      if (!deleteCollidingObjects) {
        const collidingAssets = [];
        for (const asset of Object.values(session.assets)) {
          if (
            asset.id >= values.settings.assetIdInterval[0] &&
            asset.id <= values.settings.assetIdInterval[1]
          ) {
            collidingAssets.push(asset);
          }
        }

        if (collidingAssets.length > 0) {
          addNotification({
            severity: "error",
            message: `${collidingAssets.length} Assets collide with your asset ID interval and are already in use.`,
          });
          console.log(collidingAssets);
          return;
        }

        const collidingContainers = [];
        for (const container of session.containers) {
          if (
            container.id! >= values.settings.containerIdInterval[0] &&
            container.id! <= values.settings.containerIdInterval[1]
          ) {
            collidingContainers.push(container);
          }
        }
        if (collidingContainers.length > 0) {
          addNotification({
            severity: "error",
            message: `${collidingContainers.length} Containers collide with your container ID interval and are already in use.`,
          });
          console.log(collidingContainers);
          return;
        }
      }

      setSubmitDisabled(true);
      const res = await axios.post(
        process.env.REACT_APP_API_URL + "/mirrors/create",
        { ...values, deleteObjectCollision: deleteCollidingObjects },
        { withCredentials: true },
      );
      setSubmitDisabled(false);
      if (res.data.success) {
        setOpen(false);
        addNotification({
          severity: "success",
          message: "Mirror created successfully",
        });
        session.setMirrors([...session.mirrors, res.data.response]);
      } else {
        addNotification({
          severity: "error",
          message: "Failed to create mirror",
        });
        console.error("Error creating mirror: ", res.data);
      }
    };

    const initialValues: Mirror = {
      id: -1,
      name: "",
      scriptPath: "",
      settings: {
        enabled: false,
        interval: 5,
        lastRun: 0,
        lastError: "",
        lastSuccess: 0,
        assetIdInterval: [-1, -1],
        containerIdInterval: [-1, -1],
      },
    };

    return (
      <div>
        <Button variant="contained" onClick={() => setOpen(true)}>
          <Add />
        </Button>
        <Dialog fullWidth onClose={() => setOpen(false)} open={open}>
          <DialogTitle>Create Mirror</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Fill the form to create a new mirror
            </DialogContentText>
            <Formik
              onSubmit={submit}
              initialValues={initialValues}
              validationSchema={validationSchema}
            >
              {({
                values,
                handleChange,
                handleBlur,
                handleSubmit,
                errors,
                touched,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Box display="flex" flexDirection="column" m="0 1rem">
                    <TextField
                      variant="filled"
                      type="text"
                      label="Name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.name}
                      name="name"
                      error={!!touched.name && !!errors.name}
                      helperText={touched.name && errors.name}
                      sx={{ borderRadius: "10px", margin: "10px 0" }}
                    />
                  </Box>

                  <Box display="flex" flexDirection="column" m="0 1rem">
                    <TextField
                      variant="filled"
                      type="text"
                      label="Script Path"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.scriptPath}
                      name="scriptPath"
                      error={!!touched.scriptPath && !!errors.scriptPath}
                      helperText={touched.scriptPath && errors.scriptPath}
                      sx={{ borderRadius: "10px", margin: "10px 0" }}
                    />
                  </Box>

                  <Box
                    display="flex"
                    sx={{ mx: 2 }}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="h6">
                      Delete Assets and Containers that collide <br /> with the
                      asset and container interval.
                    </Typography>
                    <Switch
                      name="sendNudgeSwitch"
                      id="sendNudgeSwitch"
                      checked={deleteCollidingObjects}
                      onChange={(e, c) => setDeleteCollidingObjects(c)}
                    />
                  </Box>

                  <Box display="flex" flexDirection="column" m="0 1rem">
                    <label>Minimum Asset ID</label>
                    <TextField
                      type="number"
                      name="settings.assetIdInterval[0]"
                      value={values.settings.assetIdInterval[0]}
                      error={
                        !!getIn(touched, "settings.assetIdInterval[0]") &&
                        !!getIn(errors, "settings.assetIdInterval[0]")
                      }
                      helperText={
                        getIn(touched, "settings.assetIdInterval[0]") &&
                        getIn(errors, "settings.assetIdInterval[0]")
                      }
                      onChange={handleChange}
                    />
                  </Box>

                  <Box display="flex" flexDirection="column" m="0 1rem">
                    <label>Maximum Asset ID</label>
                    <TextField
                      type="number"
                      name="settings.assetIdInterval[1]"
                      value={values.settings.assetIdInterval[1]}
                      error={
                        !!getIn(touched, "settings.assetIdInterval[1]") &&
                        !!getIn(errors, "settings.assetIdInterval[1]")
                      }
                      helperText={
                        getIn(touched, "settings.assetIdInterval[1]") &&
                        getIn(errors, "settings.assetIdInterval[1]")
                      }
                      onChange={handleChange}
                    />
                  </Box>

                  <Box display="flex" flexDirection="column" m="0 1rem">
                    <label>Minimum Container ID</label>
                    <TextField
                      type="number"
                      name="settings.containerIdInterval[0]"
                      value={values.settings.containerIdInterval[0]}
                      error={
                        !!getIn(touched, "settings.containerIdInterval[0]") &&
                        !!getIn(errors, "settings.containerIdInterval[0]")
                      }
                      helperText={
                        getIn(touched, "settings.containerIdInterval[0]") &&
                        getIn(errors, "settings.containerIdInterval[0]")
                      }
                      onChange={handleChange}
                    />
                  </Box>
                  <Box display="flex" flexDirection="column" m="0 1rem">
                    <label>Maximum Container ID</label>
                    <TextField
                      type="number"
                      name="settings.containerIdInterval[1]"
                      value={values.settings.containerIdInterval[1]}
                      error={
                        !!getIn(touched, "settings.containerIdInterval[1]") &&
                        !!getIn(errors, "settings.containerIdInterval[1]")
                      }
                      helperText={
                        getIn(touched, "settings.containerIdInterval[1]") &&
                        getIn(errors, "settings.containerIdInterval[1]")
                      }
                      onChange={handleChange}
                    />
                  </Box>

                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    mt="20px"
                  >
                    {submitDisabled && (
                      <Typography>
                        Please wait while we process your request. This can take
                        several minutes
                      </Typography>
                    )}
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitDisabled}
                    >
                      {submitDisabled ? (
                        <CircularProgress size={24} color="secondary" />
                      ) : (
                        "Submit"
                      )}
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>
          </DialogContent>
        </Dialog>
      </div>
    );
  },
  Edit: ({ mirror }: { mirror: Mirror }) => {
    const session = useContext(PanelSessionContext);

    const [open, setOpen] = useState(false);

    const submit = async (values: Mirror) => {
      // check if assetIdInterval and containerIdInterval are colliding with existing mirrors
      for (const m of session.mirrors.filter((m) => m.id !== mirror.id)) {
        if (
          (values.settings.assetIdInterval[0] <=
            m.settings.assetIdInterval[1] &&
            values.settings.assetIdInterval[1] >=
              m.settings.assetIdInterval[0]) ||
          (values.settings.containerIdInterval[0] <=
            m.settings.containerIdInterval[1] &&
            values.settings.containerIdInterval[1] >=
              m.settings.containerIdInterval[0])
        ) {
          addNotification({
            severity: "error",
            message:
              "Asset ID or Container ID intervals collide with an existing mirror.",
          });
          return;
        }
      }

      const res = await axios.post(
        process.env.REACT_APP_API_URL + "/mirrors/edit",
        {
          id: values.id,
          name: values.name,
          scriptPath: values.scriptPath,
          assetIdInterval: values.settings.assetIdInterval,
          containerIdInterval: values.settings.containerIdInterval,
        },
        { withCredentials: true },
      );
      if (res.data.success) {
        setOpen(false);
        addNotification({
          severity: "success",
          message: "Mirror edited successfully",
        });
        session.setMirrors([
          ...session.mirrors.filter((m) => m.id !== mirror.id),
          res.data.response,
        ]);
      } else {
        addNotification({
          severity: "error",
          message: "Failed to edit mirror",
        });
        console.error("Error editing mirror: ", res.data);
      }
    };

    const initialValues: Mirror = mirror;

    return (
      <div>
        <Button variant="contained" onClick={() => setOpen(true)}>
          <Edit />
        </Button>
        <Dialog
          fullWidth
          onClose={() => setOpen(false)}
          open={open && !!mirror}
        >
          <DialogTitle>Edit Mirror</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Fill the form to edit the mirror
            </DialogContentText>
            <Formik
              onSubmit={submit}
              initialValues={initialValues}
              validationSchema={validationSchema}
            >
              {({
                values,
                handleChange,
                handleBlur,
                handleSubmit,
                errors,
                touched,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Box display="flex" flexDirection="column" m="0 1rem">
                    <TextField
                      variant="filled"
                      type="text"
                      label="Name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.name}
                      name="name"
                      error={!!touched.name && !!errors.name}
                      helperText={touched.name && errors.name}
                      sx={{ borderRadius: "10px", margin: "10px 0" }}
                    />
                  </Box>

                  <Box display="flex" flexDirection="column" m="0 1rem">
                    <TextField
                      variant="filled"
                      type="text"
                      label="Script Path"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.scriptPath}
                      name="scriptPath"
                      error={!!touched.scriptPath && !!errors.scriptPath}
                      helperText={touched.scriptPath && errors.scriptPath}
                      sx={{ borderRadius: "10px", margin: "10px 0" }}
                    />
                  </Box>

                  <Box display="flex" justifyContent="center" mt="20px">
                    <Button type="submit" variant="contained">
                      Submit
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>
          </DialogContent>
        </Dialog>
      </div>
    );
  },
  Delete: ({ ids }: { ids: number[] }) => {
    const session = useContext(PanelSessionContext);
    const [open, setOpen] = useState(false);

    const submit = async () => {
      const res = await axios.post(
        process.env.REACT_APP_API_URL + "/mirrors/delete",
        { ids },
        { withCredentials: true },
      );
      if (res.data.success) {
        setOpen(false);
        addNotification({
          severity: "success",
          message: "Mirror(s) deleted successfully",
        });
        session.setMirrors(session.mirrors.filter((m) => !ids.includes(m.id)));
        session.setAssets(res.data.assets);
        session.setContainers(res.data.containers);
      } else {
        addNotification({
          severity: "error",
          message: "Failed to delete mirror(s)",
        });
        console.log("Error deleting mirror(s): ", res.data);
      }
    };

    return (
      <div>
        <Button variant="contained" onClick={() => setOpen(true)}>
          <Remove />
        </Button>
        <Dialog open={open} onClose={() => setOpen(false)}>
          <DialogTitle>Delete Mirror(s)</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the selected mirror(s)? This
              action cannot be undone.
            </DialogContentText>
            <Box display="flex" justifyContent="center" mt="20px">
              <Button variant="contained" color="error" onClick={submit}>
                Delete
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      </div>
    );
  },
  Sync: ({ mirror }: { mirror: Mirror }) => {
    const session = useContext(PanelSessionContext);
    const [open, setOpen] = useState(false);

    const [syncing, setSyncing] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState("Disconnected");
    const [status, setStatus] = useState("");
    const wsRef = useRef<WebSocket | null>(null);

    useEffect(() => {
      if (open) {
        const opened = moment().unix();
        console.log(process.env.REACT_APP_API_WS_URL, process.env);
        wsRef.current = new WebSocket(process.env.REACT_APP_API_WS_URL + "/");

        wsRef.current.onopen = () => {
          setConnectionStatus("Connected");
          setStatus("Waiting for sync...");
        };

        wsRef.current.onmessage = (event) => {
          console.log("Message from server: ", event.data);
          setStatus(event.data);
        };

        wsRef.current.onclose = () => {
          if (moment().unix() - opened > 3) {
            window.location.reload();
          }
        };
        return () => {
          wsRef.current?.close();
          setSyncing(false);
        };
      }
    }, [open]);

    return (
      <div>
        <Button variant="contained" onClick={() => setOpen(true)}>
          <Sync />
        </Button>
        <Dialog open={open} onClose={() => setOpen(false)}>
          <DialogTitle>Sync Mirror</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Press Sync now to execute the script and start pulling data from
              the mirror.
            </DialogContentText>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              mt="20px"
            >
              <Button
                variant="contained"
                onClick={() => {
                  if (wsRef.current?.readyState === WebSocket.OPEN) {
                    setSyncing(true);
                    wsRef.current.send(
                      JSON.stringify({ action: "sync", mirrorId: mirror.id }),
                    );
                  }
                }}
                disabled={syncing || connectionStatus !== "Connected"}
              >
                Sync
              </Button>
              <Typography variant="h6" color="textSecondary" mt="10px">
                {status}
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
      </div>
    );
  },
};
